"use client";

import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function ArchiveConsumer() {
  const {
    posts,
    loading,
    error,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage,
    refetch
  } = usePosts();
  const [topic, setTopic] = useState<string>("all");
  const [year, setYear] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("date-desc");
  const years = useMemo(() => {
    const yearSet = new Set<string>();
    posts.forEach(post => {
      if (post.date) {
        yearSet.add(new Date(post.date).getFullYear().toString());
      }
    });
    return Array.from(yearSet).sort((a, b) => Number(b) - Number(a));
  }, [posts]);
  const filteredPosts = useMemo(() => {
    let result = [...posts];
    if (topic !== "all") {
      result = result.filter(post => post.categories?.some(cat => cat.slug.toLowerCase() === topic.toLowerCase() || cat.name.toLowerCase() === topic.toLowerCase()));
    }
    if (year !== "all") {
      result = result.filter(post => post.date && new Date(post.date).getFullYear().toString() === year);
    }
    if (sortBy === "date-desc") {
      result.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
    } else if (sortBy === "date-asc") {
      result.sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
    } else if (sortBy === "title") {
      result.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortBy === "category") {
      result.sort((a, b) => (a.categories?.[0]?.name || "").localeCompare(b.categories?.[0]?.name || ""));
    }
    return result;
  }, [posts, topic, year, sortBy]);
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric"
    }).toUpperCase();
  };
  const estimateReadTime = (content?: string) => {
    if (!content) return "1 MIN";
    const words = content.replace(/<[^>]*>/g, "").split(/\s+/).length;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return `${minutes} MIN`;
  };
  return <div>
      {}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-4 pb-6 border-b border-border">
        <div className="md:col-span-7">
          <ToggleGroup type="single" value={topic} onValueChange={v => v && setTopic(v)} className="flex flex-wrap gap-x-6 gap-y-2 justify-start">
            {["all", "justice", "community", "policy", "culture"].map((t, i) => <ToggleGroupItem data-index={i} key={t} value={t} className="bg-transparent border-0 px-0 py-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground hover:bg-transparent hover:text-foreground data-[state=on]:bg-transparent data-[state=on]:text-foreground data-[state=on]:underline data-[state=on]:decoration-primary data-[state=on]:decoration-2 data-[state=on]:underline-offset-[6px] transition-colors">
                {t === "all" ? "All" : t.charAt(0).toUpperCase() + t.slice(1)}
              </ToggleGroupItem>)}
          </ToggleGroup>
        </div>

        <div className="md:col-span-5 flex flex-col sm:flex-row gap-4 md:justify-end">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="border-0 border-b border-border bg-transparent text-xs font-medium uppercase tracking-[0.05em] text-foreground h-auto py-2 px-0 hover:border-foreground transition-colors focus-visible:ring-0 min-w-[140px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map((y, i) => <SelectItem data-index={i} key={y} value={y}>
                  {y}
                </SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="border-0 border-b border-border bg-transparent text-xs font-medium uppercase tracking-[0.05em] text-foreground h-auto py-2 px-0 hover:border-foreground transition-colors focus-visible:ring-0 min-w-[160px]">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Date ↓</SelectItem>
              <SelectItem value="date-asc">Date ↑</SelectItem>
              <SelectItem value="title">Title</SelectItem>
              <SelectItem value="category">Category</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {}
      <div className="h-12" />

      {}
      {loading ? <div className="space-y-0">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-20 border-b border-border bg-muted/30 animate-pulse" />)}
        </div> : error ? <div className="text-sm text-muted-foreground py-12 text-center">
          Failed to load reports.{" "}
          <Button variant="ghost" size="sm" onClick={refetch} className="p-0 h-auto underline decoration-primary underline-offset-4 hover:bg-transparent">
            Retry
          </Button>
        </div> : filteredPosts.length === 0 ? <p className="text-muted-foreground py-12 text-center text-sm uppercase tracking-[0.05em]">No reports match the selected filters.</p> : <div className="border-t border-border">
          {filteredPosts.map((post, i) => {
        const issueNum = `DOC ${String(filteredPosts.length - i).padStart(2, "0")}`;
        const categoryName = post.categories?.[0]?.name?.toUpperCase() || "NEWS";
        const readTime = estimateReadTime(post.content || post.excerpt);
        return <Link key={post.id} to={post.link || "#"} data-index={i} className="block border-b border-border group transition-colors duration-200 hover:bg-accent/50">
                {}
                <div className="hidden md:grid grid-cols-12 gap-4 py-7 items-center">
                  <div className="col-span-2 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    {issueNum}
                  </div>
                  <div className="col-span-2 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    {formatDate(post.date)}
                  </div>
                  <div className="col-span-5 font-default text-lg md:text-xl font-semibold text-foreground tracking-tight transition-transform duration-200 group-hover:translate-x-2 relative">
                    <span className="relative inline-block">
                      {post.title}
                      <span className="absolute left-0 -bottom-0.5 h-px bg-primary w-0 group-hover:w-full transition-all duration-300 ease-out" />
                    </span>
                  </div>
                  <div className="col-span-2 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground text-right">
                    {categoryName}
                  </div>
                  <div className="col-span-1 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground text-right">
                    {readTime}
                  </div>
                </div>

                {}
                <div className="md:hidden py-6 space-y-2">
                  <div className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    {issueNum} · {formatDate(post.date)}
                  </div>
                  <div className="font-default text-lg font-semibold text-foreground tracking-tight">
                    {post.title}
                  </div>
                  <div className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    {categoryName} · {readTime}
                  </div>
                </div>
              </Link>;
      })}
        </div>}

      {}
      {!loading && !error && totalPages > 1 && <div className="mt-16 flex items-center gap-6">
          <Button variant="ghost" size="sm" onClick={prevPage} disabled={!hasPrev} className="text-xs font-medium uppercase tracking-[0.05em] text-foreground hover:bg-transparent hover:text-foreground disabled:opacity-30 p-0 h-auto gap-1">
            <ChevronLeft className="size-4" />
            Prev
          </Button>

          <div className="flex items-center gap-4">
            {Array.from({
          length: totalPages
        }).map((_, i) => {
          const pageNum = i + 1;
          const isActive = pageNum === currentPage;
          return <Button data-index={i} key={pageNum} variant="ghost" size="sm" onClick={() => setPage(pageNum)} className={`text-xs font-medium uppercase tracking-[0.05em] transition-colors p-0 h-auto rounded-none hover:bg-transparent ${isActive ? "text-foreground underline decoration-primary decoration-2 underline-offset-[6px]" : "text-muted-foreground hover:text-foreground"}`}>
                  {String(pageNum).padStart(2, "0")}
                </Button>;
        })}
          </div>

          <Button variant="ghost" size="sm" onClick={nextPage} disabled={!hasNext} className="text-xs font-medium uppercase tracking-[0.05em] text-foreground hover:bg-transparent hover:text-foreground disabled:opacity-30 p-0 h-auto gap-1">
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>}
    </div>;
}
export default function Reports() {
  const wp_query_params = {
    post_type: "post",
    per_page: 10,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="10748" id="the-latest-reports" className="bg-background text-foreground border-t border-border py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-5 md:px-12">
        {}
        <div className="mb-16 md:mb-20 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8">
            <div className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground mb-4">
              The Archive
            </div>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tighter leading-[0.9]">
              Latest{" "}
              <span className="relative inline-block">
                Reports
                <span className="absolute left-0 -bottom-1 h-[2px] bg-primary w-full" />
              </span>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex md:items-end">
            <p className="text-base text-muted-foreground leading-[1.6] max-w-md">
              A comprehensive collection of past research, investigative journalism, and community-driven studies from Inclusive Voices News.
            </p>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 10,
        order: "desc",
        orderby: "date"
      }}>
          <ArchiveConsumer />
        </PostsProvider>
      </div>
    </section>;
}